import React, { useEffect } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Map as MapIcon } from '@mui/icons-material';
import Constants from '../../../../common/constants/Constants';
import Tile from '../../../../common/components/Tile/Tile';

var map;
var dataSource;
var popup;

const authOptions = {
  authType: atlas.AuthenticationType.subscriptionKey,
  subscriptionKey: Constants.MAPS_API_KEY,
};

const AzureMap = (props) => {
  useEffect(() => {
    map = new atlas.Map('myMap', {
      authOptions: authOptions,
      renderWorldCopies: false,
      language: 'en-US',
      showLogo: false,
      maxZoom: 21.9, //in combination with datasource's (cluster)maxZoom properties, prevents really close points from de-clustering
    });

    map.setUserInteraction({ /* dblClickZoomInteraction: false, */ wheelZoomRate: 1 / 200 });

    map.events.add('ready', function () {
      var filteredData = props.data.filter(
        (el) => (el.Latitude || el.Latitude == 0) && (el.Longitude || el.Longitude == 0)
      );
      prepareData(filteredData);
      addControls();
      centerMap();

      var iconPromises = configureCustomIcons();
      Promise.all(iconPromises).then(() => {
        addLayers();
        addEvents();
      });
    });
  }, []);

  return (
    <Tile className="u-mb-full" title="Instrument map" TitleIcon={MapIcon} helpTag="dashboard-map">
      <div
        id="myMap"
        style={{ height: '30rem', background: '#f0f0f0', border: '1px solid #aaa' }}
        data-qa="dashboard-map"
      />
    </Tile>
  );
};

const prepareData = (filteredData) => {
  dataSource = new atlas.source.DataSource(null, {
    cluster: true,
    clusterRadius: 30, //results in more detailed clustering
    clusterMaxZoom: 22,
    maxZoom: 22,
    clusterProperties: {
      'amount black': [
        '+',
        ['case', ['==', ['get', 'color'], Constants.MAP_SEVERITY_COLORS.BLACK], 1, 0],
      ],
      'amount green': [
        '+',
        ['case', ['==', ['get', 'color'], Constants.MAP_SEVERITY_COLORS.GREEN], 1, 0],
      ],
      'amount grey': [
        '+',
        ['case', ['==', ['get', 'color'], Constants.MAP_SEVERITY_COLORS.GREY], 1, 0],
      ],
      'amount orange': [
        '+',
        ['case', ['==', ['get', 'color'], Constants.MAP_SEVERITY_COLORS.ORANGE], 1, 0],
      ],
      'amount red': [
        '+',
        ['case', ['==', ['get', 'color'], Constants.MAP_SEVERITY_COLORS.RED], 1, 0],
      ],
    },
  });
  filteredData.forEach((instrument) => {
    dataSource.add(
      new atlas.data.Feature(new atlas.data.Point([instrument.Longitude, instrument.Latitude]), {
        serialNumber: instrument.SerialNumber,
        type: instrument.Type,
        color: instrument.OverallAlert
          ? instrument.OverallAlert.Color
          : Constants.MAP_SEVERITY_COLORS.GREY,
      })
    );
  });
  map.sources.add(dataSource);
};

const addControls = () => {
  map.controls.add([new atlas.control.StyleControl(), new atlas.control.ZoomControl()], {
    position: 'top-right',
  });
};

const configureCustomIcons = () => {
  //Create an array of custom icon promises to load into the map.
  var iconPromises = [
    map.imageSprite.createFromTemplate(
      'greenIconId',
      'pin-round', //see https://learn.microsoft.com/en-us/azure/azure-maps/how-to-use-image-templates-web-sdk#list-of-image-templates for all available marker shapes
      Constants.MAP_SEVERITY_COLORS.GREEN
    ),
    map.imageSprite.createFromTemplate(
      'orangeIconId',
      'pin-round',
      Constants.MAP_SEVERITY_COLORS.ORANGE
    ),
    map.imageSprite.createFromTemplate('redIconId', 'pin-round', Constants.MAP_SEVERITY_COLORS.RED),
    map.imageSprite.createFromTemplate(
      'blackIconId',
      'pin-round',
      Constants.MAP_SEVERITY_COLORS.BLACK
    ),
    map.imageSprite.createFromTemplate(
      'greyIconId',
      'pin-round',
      Constants.MAP_SEVERITY_COLORS.GREY
    ),
  ];

  return iconPromises;
};

const addLayers = () => {
  //Create a bubble layer for rendering clustered data points.
  var bubbleLayer = new atlas.layer.BubbleLayer(dataSource, 'bubbleLayerId', {
    radius: 15,
    color: [
      'case',
      ['>=', ['get', 'amount black'], 1],
      Constants.MAP_SEVERITY_COLORS.BLACK,
      ['>=', ['get', 'amount red'], 1],
      Constants.MAP_SEVERITY_COLORS.RED,
      ['>=', ['get', 'amount orange'], 1],
      Constants.MAP_SEVERITY_COLORS.ORANGE,
      ['>=', ['get', 'amount green'], 1],
      Constants.MAP_SEVERITY_COLORS.GREEN,
      Constants.MAP_SEVERITY_COLORS.GREY,
    ],
    //opacity: 0.9,
    //strokeWidth: 0, //cluster border
    filter: ['has', 'point_count'], //Only rendered data points that have a point_count property, which clusters do.
  });

  //Create a symbol layer to render the count of locations in a cluster.
  var numberLayer = new atlas.layer.SymbolLayer(dataSource, 'numberLayerId', {
    iconOptions: {
      image: 'none', //Hide the icon image.
      allowOverlap: true,
    },
    textOptions: {
      textField: ['get', 'point_count_abbreviated'],
      color: '#ffffff',
      offset: [0, 0.4],
      allowOverlap: true,
    },
  });

  //Create a layer to render the individual locations
  var symbolLayer = new atlas.layer.SymbolLayer(dataSource, 'symbolLayerId', {
    filter: ['!', ['has', 'point_count']], //Filter out clustered points from this layer.
    textOptions: {
      textField: ['to-string', ['get', 'serialNumber']],
      offset: [0, 0.8],
      allowOverlap: true,
    },
    iconOptions: {
      allowOverlap: true,
      //Use a match expression to select the image icon based on the EntityType property of the data point.
      image: [
        'match',
        ['get', 'color'],
        Constants.MAP_SEVERITY_COLORS.GREEN,
        'greenIconId',
        Constants.MAP_SEVERITY_COLORS.ORANGE,
        'orangeIconId',
        Constants.MAP_SEVERITY_COLORS.RED,
        'redIconId',
        Constants.MAP_SEVERITY_COLORS.BLACK,
        'blackIconId',
        //Default fallback icon.
        'greyIconId',
      ],
    },
  });
  //Create layers for rendering clusters, their counts and unclustered points and add the layers to the map.
  map.layers.add([bubbleLayer, numberLayer, symbolLayer]);
};

const addEvents = () => {
  popup = new atlas.Popup();

  //When the mouse is over the layer, change the cursor to be a pointer.
  map.events.add(
    'mouseover',
    [map.layers.getLayerById('symbolLayerId'), map.layers.getLayerById('bubbleLayerId')],
    function () {
      map.getCanvasContainer().style.cursor = 'pointer';
    }
  );
  map.events.add(
    'mouseout',
    [map.layers.getLayerById('symbolLayerId'), map.layers.getLayerById('bubbleLayerId')],
    function () {
      map.getCanvasContainer().style.cursor = 'grab';
    }
  );

  //Open popup when clicking on a point
  map.events.add('click', map.layers.getLayerById('bubbleLayerId'), function (e) {
    createClusterPopup(e);
  });
  map.events.add('click', map.layers.getLayerById('symbolLayerId'), function (e) {
    createMarkerPopup(e);
  });

  //Close popup when moving the map
  const closePopupEvents = ['movestart', 'move', 'moveend', 'dblclick'];
  closePopupEvents.forEach((closeEvent) => {
    map.events.add(closeEvent, function (e) {
      popup.close(map);
    });
  });
};

const createClusterPopup = (e) => {
  const clusterId = e.shapes[0].properties.cluster_id;
  var instruments = [];
  var content = [];
  var coordinates = e.shapes[0].geometry.coordinates; //get cluster coordinates
  var alertradius = 5;

  //Make sure that the point exists.
  if (e.shapes && e.shapes.length > 0) {
    dataSource.getClusterLeaves(clusterId, Infinity, 0).then((leaves) => {
      leaves.forEach((leave) => {
        instruments.push(leave.getProperties());
      });

      var sortedInstruments = sortInstruments(instruments);

      //Create a list of all pushpins that are in the cluster.
      content.push(
        '<div data-qa="map-infoBox" style="width:165px;max-height:115px;overflow-y:auto;"><h6 style="margin-left:10px; margin-top: 10px;">Cluster composition</h6><table style="border-spacing: 5px 0px; border-collapse: separate; margin-left: 5px;">'
      );
      sortedInstruments.forEach((instrument) => {
        content.push(
          `<tr><td>
            <a href="/instrument-details/${instrument.serialNumber}">
              ${instrument.serialNumber}
            </a></td>
            <td><svg xmlns="http://www.w3.org/2000/svg" width="${alertradius * 2}" height="${
            alertradius * 2
          }">
              <circle cx="${alertradius}" cy="${alertradius}" r="${alertradius}" fill="${
            instrument.color
          }"/>
            </svg></td>
          </tr>`
        );
      });
      content.push('</table></div>');

      popup.setOptions({
        content: content.join(''),
        position: coordinates,
        pixelOffset: [0, -0],
      });
      popup.open(map);
    });
  }
};

const createMarkerPopup = (e) => {
  //Make sure that the point exists.
  if (e.shapes && e.shapes.length > 0) {
    var coordinate = e.shapes[0].getCoordinates();
    var instrument = e.shapes[0].getProperties();
    var content = `
      <div data-qa="map-infoBox" style="width:165px;max-height:115px;overflow-y:auto; margin: 10px;">
        <h6>${instrument.type || 'No title'}</h6>
        <p>
          Serial number: ${instrument.serialNumber}
        </p>
        <p>
          <a href="/instrument-details/${instrument.serialNumber}">
            View instrument details
          </a>
        </p>
      </div>
    `;

    popup.setOptions({
      content: content,
      position: coordinate,
      pixelOffset: [0, -10],
    });
    popup.open(map);
  }
};

const sortInstruments = (instruments) => {
  let sortingScheme = [
    Constants.MAP_SEVERITY_COLORS.BLACK,
    Constants.MAP_SEVERITY_COLORS.RED,
    Constants.MAP_SEVERITY_COLORS.ORANGE,
    Constants.MAP_SEVERITY_COLORS.GREEN,
    Constants.MAP_SEVERITY_COLORS.GREY,
  ];
  let sortedPushpins = [];

  sortingScheme.forEach((color) => {
    let temp = [];
    temp = instruments.filter((instrument) => instrument.color == color);
    temp.sort(function (a, b) {
      var snA = a.serialNumber.toUpperCase();
      var snB = b.serialNumber.toUpperCase();
      if (snA < snB) return -1;
      if (snA > snB) return 1;
      return 0;
    });
    sortedPushpins.push(...temp);
  });
  return sortedPushpins;
};

const centerMap = () => {
  var bounds = atlas.data.BoundingBox.fromData(dataSource.getShapes());

  if (bounds !== null) {
    var w = atlas.data.BoundingBox.getWidth(bounds);
    var h = atlas.data.BoundingBox.getHeight(bounds);

    //If the bounding box is really small, likely a single point, use center/zoom.
    if (w < 0.000001 || h < 0.000001) {
      map.setCamera({
        center: atlas.data.BoundingBox.getCenter(bounds),
        zoom: 17,
      });
    } else {
      map.setCamera({
        bounds: bounds,
        padding: 30,
      });
    }
  }
};

export default AzureMap;
